import { localizeInfectionPreventionTerminology } from '../../utils/sector.js';

const LTC_RETIREMENT_PROGRAM_HASHES = [
    'abx-stewardship',
    'accreditation',
    'aros',
    'construction',
    'dietary',
    'environmental',
    'foot-care',
    'gastro',
    'hand-hygiene',
    'ipac-champions',
    'ipac-committee',
    'respiratory',
    'uti',
    'wounds',
];

const CLINICAL_PROGRAM_HASHES = [
    'abx-stewardship',
    'aros',
    'construction',
    'environmental',
    'clabsi',
    'hand-hygiene',
    'immunization',
    'bsi',
    'vaps',
    'cauti',
    'ssi',
];

export const PROGRAM_HASHES_BY_SECTOR = {
    Clinical: CLINICAL_PROGRAM_HASHES,
    'Long-Term Care': LTC_RETIREMENT_PROGRAM_HASHES,
    Retirement: LTC_RETIREMENT_PROGRAM_HASHES,
};

export const ALL_PROGRAMS = [
    {
        hash: 'abx-stewardship',
        title: 'ABX Stewardship',
        icon: 'fi-pills',
        to: '/program/abx-stewardship',
    },
    {
        hash: 'accreditation',
        title: 'Accreditation',
        icon: 'fi-award',
        to: '/program/accreditation',
    },
    {
        hash: 'aros',
        title: 'MDROs',
        icon: 'fi-virus',
        to: '/program/aros',
    },
    {
        hash: 'construction',
        title: 'Construction',
        icon: 'fi-hard-hat',
        to: '/program/construction',
    },
    {
        hash: 'dietary',
        title: 'Dietary',
        icon: 'fi-meal',
        to: '/program/dietary',
    },
    {
        hash: 'environmental',
        title: 'Environmental',
        icon: 'fi-leaf',
        to: '/program/environmental',
    },
    {
        hash: 'foot-care',
        title: 'Foot Care',
        icon: 'fi-foot',
        to: '/program/foot-care',
    },
    {
        hash: 'gastro',
        title: 'Gastro',
        icon: 'fi-stomach',
        to: '/program/gastro',
    },
    {
        hash: 'hand-hygiene',
        title: 'Hand Hygiene',
        icon: 'fi-washing-hands',
        to: '/program/hand-hygiene',
    },
    {
        hash: 'ipac-champions',
        title: 'IPAC Champions',
        icon: 'fi-trophy',
        to: '/program/ipac-champions',
    },
    {
        hash: 'ipac-committee',
        title: 'IPAC Committee',
        icon: 'fi-users',
        to: '/program/ipac-committee',
    },
    {
        hash: 'respiratory',
        title: 'Respiratory',
        icon: 'fi-lungs',
        to: '/program/respiratory',
    },
    {
        hash: 'uti',
        title: 'UTI',
        icon: 'fi-bladder',
        to: '/program/uti',
    },
    {
        hash: 'wounds',
        title: 'Wounds',
        icon: 'fi-bandage',
        to: '/program/wounds',
    },
    {
        hash: 'clabsi',
        title: 'CLABSI',
        icon: 'fi-virus',
        to: '/program/clabsi',
    },
    {
        hash: 'immunization',
        title: 'Immunization',
        icon: 'fi-syringe',
        to: '/program/immunization',
    },
    {
        hash: 'bsi',
        title: 'BSI',
        icon: 'fi-health-safety',
        to: '/program/bsi',
    },
    {
        hash: 'vaps',
        title: 'VAPs',
        icon: 'fi-lungs',
        to: '/program/vaps',
    },
    {
        hash: 'cauti',
        title: 'CAUTI',
        icon: 'fi-bladder',
        to: '/program/cauti',
    },
    {
        hash: 'ssi',
        title: 'SSI',
        icon: 'fi-bandage',
        to: '/program/ssi',
    },
];

const PROGRAMS_BY_HASH = ALL_PROGRAMS.reduce((acc, program) => {
    acc[program.hash] = program;
    return acc;
}, {});

/** @deprecated Use getProgramsForSector() for sector-specific lists. */
export const PROGRAMS = ALL_PROGRAMS;

export function getProgramHashesForSector(sectorName) {
    return (
        PROGRAM_HASHES_BY_SECTOR[sectorName] ??
        PROGRAM_HASHES_BY_SECTOR['Long-Term Care']
    );
}

export function localizeProgramTitle(title, countryCode) {
    return localizeInfectionPreventionTerminology(title, countryCode);
}

function withLocalizedProgram(program, countryCode) {
    if (!countryCode) {
        return program;
    }

    return {
        ...program,
        title: localizeProgramTitle(program.title, countryCode),
    };
}

export function getProgramsForSector(sectorName, countryCode) {
    return getProgramHashesForSector(sectorName)
        .map((hash) => PROGRAMS_BY_HASH[hash])
        .filter(Boolean)
        .map((program) => withLocalizedProgram(program, countryCode));
}

export function getProgramOptionsForSector(sectorName, countryCode) {
    return getProgramsForSector(sectorName, countryCode).map((program) => ({
        text: program.title,
        value: program.hash,
    }));
}

export function getProgramFilterOptionsForSector(sectorName, countryCode) {
    return getProgramsForSector(sectorName, countryCode).map((program) => ({
        label: program.title,
        value: program.hash,
    }));
}

export function getProgramOptionsForSectors(sectorNames = [], countryCode) {
    const hashes = new Set();
    sectorNames.forEach((sectorName) => {
        getProgramHashesForSector(sectorName).forEach((hash) =>
            hashes.add(hash)
        );
    });

    return ALL_PROGRAMS.filter((program) => hashes.has(program.hash)).map(
        (program) => ({
            text: localizeProgramTitle(program.title, countryCode),
            value: program.hash,
        })
    );
}

export function getProgramByHash(hash) {
    return PROGRAMS_BY_HASH[hash] ?? null;
}

export function getProgramLabel(hash, countryCode) {
    const program = getProgramByHash(hash);
    if (!program) {
        return hash;
    }

    return localizeProgramTitle(program.title, countryCode);
}

/** @deprecated Use getProgramOptionsForSector() for sector-specific options. */
export const PROGRAM_OPTIONS = getProgramOptionsForSector('Long-Term Care');

const PROGRAM_FAVOURITES_KEY = 'program_favourites';

export const getProgramFavourites = () => {
    try {
        const stored = localStorage.getItem(PROGRAM_FAVOURITES_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
};

export const isProgramFavourite = (hash) => {
    return getProgramFavourites().includes(hash);
};

export const toggleProgramFavourite = (hash) => {
    const favourites = getProgramFavourites();
    const index = favourites.indexOf(hash);
    if (index > -1) {
        favourites.splice(index, 1);
    } else {
        favourites.push(hash);
    }
    localStorage.setItem(PROGRAM_FAVOURITES_KEY, JSON.stringify(favourites));
    return favourites.includes(hash);
};
